import { ConvexError } from 'convex/values';
import type { MutationCtx } from '../../_generated/server';
import type { Doc, Id } from '../../_generated/dataModel';

/**
 * Normalize a display name into a URL-safe channel slug: lowercase, spaces to
 * hyphens, strip anything outside [a-z0-9-], collapse repeated hyphens. Empty
 * for names with no Latin letters or digits (see `fallbackChannelSlug`).
 */
export function normalizeSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Suffixed fallback slugs tried before giving up on a hash collision. */
export const MAX_FALLBACK_ATTEMPTS = 20;

/**
 * Stable `channel-<hash>` slug for names with no [a-z0-9] characters
 * (Japanese, Arabic, Cyrillic, emoji…). Derived from the name, so the same
 * name maps to the same slug and the duplicate-name check still applies.
 */
export function fallbackChannelSlug(name: string): string {
  const key = channelNameKey(name);
  // 32-bit FNV-1a over UTF-16 code units.
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `channel-${hash.toString(36)}`;
}

/** Case- and width-insensitive form of a channel name, for name matching. */
export function channelNameKey(name: string): string {
  return name.trim().normalize('NFKC').toLowerCase();
}

async function findBySlug(
  ctx: Pick<MutationCtx, 'db'>,
  organizationId: Id<'organizations'>,
  slug: string
): Promise<Doc<'channels'> | null> {
  return await ctx.db
    .query('channels')
    .withIndex('by_organization_and_slug', (q) =>
      q.eq('organizationId', organizationId).eq('slug', slug)
    )
    .first();
}

/**
 * Pick the slug for a channel named `name`. Latin names keep the plain
 * normalized slug; anything else gets a fallback slug. Throws
 * `CHANNEL_SLUG_TAKEN` when another channel already uses this name (the
 * client shows a generic "not available" message, so private channels are
 * not confirmed). A fallback slug that collides with a differently named
 * channel gets a numeric suffix instead of failing.
 */
export async function resolveChannelSlug(
  ctx: Pick<MutationCtx, 'db'>,
  args: {
    organizationId: Id<'organizations'>;
    name: string;
    /** The channel being renamed, which may keep its own slug. */
    selfId?: Id<'channels'>;
  }
): Promise<{ slug: string; isFallback: boolean }> {
  const plain = normalizeSlug(args.name);
  if (plain.length > 0) {
    const clash = await findBySlug(ctx, args.organizationId, plain);
    if (clash && clash._id !== args.selfId) {
      throw new ConvexError('CHANNEL_SLUG_TAKEN');
    }
    return { slug: plain, isFallback: false };
  }

  const base = fallbackChannelSlug(args.name);
  for (let attempt = 1; attempt <= MAX_FALLBACK_ATTEMPTS; attempt += 1) {
    const slug = attempt === 1 ? base : `${base}-${attempt}`;
    const clash = await findBySlug(ctx, args.organizationId, slug);
    if (!clash || clash._id === args.selfId) {
      return { slug, isFallback: true };
    }
    if (clash.name.trim() === args.name.trim()) {
      throw new ConvexError('CHANNEL_SLUG_TAKEN');
    }
  }
  throw new ConvexError('CHANNEL_SLUG_TAKEN');
}
